import {
    Alert,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import React, {useContext} from 'react'
import { Trans } from 'react-i18next'
import {
    ClinicalConditionState,
    ComplaintState,
    ProductTypeOptions,
} from '../../../contexts/types'
import { ErrorRecord } from '../../../hooks/useForm'
import productTypeIsOneOf from '../../../lib/productTypeIsOneOf'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alertFormLabel: {
            color: 'rgb(102, 60, 0)',
            '&.Mui-focused': {
                color: 'rgb(102, 60, 0)',
            },
        },
    })
)

type PermanentOptionsProps = {
    data: ClinicalConditionState
    errors: ErrorRecord<ClinicalConditionState>
    complaint: ComplaintState
    handleChange: (
        fieldName: keyof ClinicalConditionState,
        value: string
    ) => void
}

const PermanentOptions: React.FC<PermanentOptionsProps> = ({
    data,
    errors,
    complaint,
    handleChange,
}) => {
    const classes = useStyles()
    const { t, tns } = useContext(OrgCustomizationContext)
    const productType: ProductTypeOptions = complaint.generalComplaint
        .productType as ProductTypeOptions

    return (
        <>
            <Typography
                variant='body1'
                color='secondary.contrastText'
                sx={{ pt: 2 }}
            >
                <Trans i18nKey={tns('cci_permanent_long_term_conditions')}>
                    -
                    <Typography component='span' sx={{ color: 'primary.main' }}>
                        -
                    </Typography>
                    -
                </Trans>
            </Typography>
            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='DEATH'
                    control={<Radio />}
                    label={t('cci_option_death') as string}
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='NERVE_INJURY_SYMPTOMS'
                    control={<Radio />}
                    label={t('cci_option_nerve_injury_symptoms') as string}
                />
            )}

            {data.clinicalCondition === 'NERVE_INJURY_SYMPTOMS' && (
                <Alert severity='warning' icon={false}>
                    <FormControl>
                        <FormLabel classes={{ root: classes.alertFormLabel }}>
                            {t('cci_were_nerve_symptoms_alleviated')}
                        </FormLabel>
                        <RadioGroup
                            row
                            value={data.wereNerveSymptomsAlleviated}
                            onChange={(e) =>
                                handleChange(
                                    'wereNerveSymptomsAlleviated',
                                    e.target.value
                                )
                            }
                        >
                            <FormControlLabel
                                value='YES'
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                value='NO'
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        <FormHelperText
                            error={!!errors.wereNerveSymptomsAlleviated}
                        >
                            {errors.wereNerveSymptomsAlleviated}
                        </FormHelperText>
                    </FormControl>
                </Alert>
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <>
                    <FormControlLabel
                        value='PERMANENT_IMPAIRMENT'
                        control={<Radio />}
                        label={t('cci_option_permanent_impairment') as string}
                    />

                    {data.clinicalCondition === 'PERMANENT_IMPAIRMENT' && (
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={4}
                            sx={{
                                maxWidth: '40rem',
                            }}
                            name='data.permanentImpairmentExplanation'
                            error={!!errors.permanentImpairmentExplanation}
                            helperText={errors.permanentImpairmentExplanation}
                            value={data.permanentImpairmentExplanation}
                            onChange={(e) =>
                                handleChange(
                                    'permanentImpairmentExplanation',
                                    e.target.value
                                )
                            }
                        />
                    )}
                </>
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='PERMANENT_PAIN'
                    control={<Radio />}
                    label={t('cci_option_permanent_pain') as string}
                />
            )}

            <FormControlLabel
                value='OTHER'
                control={<Radio />}
                label={t('cci_option_other') as string}
            />

            {data.clinicalCondition === 'OTHER' && (
                <TextField
                    multiline
                    minRows={2}
                    maxRows={4}
                    sx={{
                        maxWidth: '40rem',
                    }}
                    name='otherExplanation'
                    error={!!errors.otherExplanation}
                    helperText={errors.otherExplanation}
                    value={data.otherExplanation}
                    onChange={(e) =>
                        handleChange('otherExplanation', e.target.value)
                    }
                />
            )}

            <FormControlLabel
                value='NONE_OF_THE_ABOVE'
                control={<Radio />}
                label={t('cci_option_none_of_the_above') as string}
            />
        </>
    )
}

export default PermanentOptions
